import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

/* React redux */
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';

/* Project */
import useForm from 'hooks/useForm';
import { setActiveStep } from 'content/app.actions';
import { darkerGreyOnForms } from 'utils/helper';
import { getUrlPreselection } from 'utils/urlPreselection';
import { controlsChanged } from '../schedulerSteps.actions';
import SchedulerStepsStyles from '../SchedulerSteps.styles';
import { getRegionIdsByVehicleBrandId, getWorkshopIdsByVehicleBrandId } from '../../../../utils/filter-data-frontend';

function LocationForm(props) {
  const { submitButtonRef, onChange, errors } = props;

  const dispatch = useDispatch();

  const regions = useSelector((state) => state.app.objects.regions);
  const workshops = useSelector((state) => state.app.objects.workshops);
  const controlRegion = useSelector((state) => state.schedulerSteps.controls.region);
  const controlWorkshop = useSelector((state) => state.schedulerSteps.controls.workshop);
  const controls = useSelector((state) => state.schedulerSteps.controls);
  const filteredRegions = getRegionIdsByVehicleBrandId(controls.brandExternalId)
  const filteredWorkshopExternalIds = getWorkshopIdsByVehicleBrandId(controls.brandExternalId);

  const onChangeText = useCallback((id, value) => {
    dispatch(controlsChanged({ name: id, value }));
  }, []);

  const onChangeSelect = useCallback(
    (event) => {
      onChange(event);
    },
    [onChange],
  );

  const onChangeRegion = useCallback(
    (event) => {
      onChangeText('workshop', '');
      onChange(event);
    },
    [onChange],
  );

  const regionsList = useMemo(
    () => Object.values(regions)
      .filter(region => filteredRegions.includes(region.externalId))
      .sort((a, b) => +a.name - +b.name),
    [regions, filteredRegions]
  )

  const workshopsList = useMemo(() => Object.values(workshops)
    .filter(workshop => filteredWorkshopExternalIds.includes(workshop.externalId)),
    [workshops]
  );

  const filteredWorkshops = useMemo(
    () => workshopsList.filter((workshop) => workshop.region.externalId === controlRegion),
    [workshops, controlRegion],
  );

  const preselectedWorkshop = useRef(getUrlPreselection().workshop);

  // The link's workshop is applied once, only if the customer has not chosen one yet and the
  // workshop serves the vehicle's brand.
  useEffect(() => {
    if (!preselectedWorkshop.current || !workshopsList.length) return;

    const workshop =
      controlWorkshop === '' &&
      workshopsList.find((item) => item.externalId === preselectedWorkshop.current);
    preselectedWorkshop.current = '';

    if (workshop) {
      onChangeText('region', workshop.region.externalId);
      onChangeText('workshop', workshop.id);
    }
  }, [workshopsList]);

  return (
    <>
      <FormControl fullWidth size="small" sx={SchedulerStepsStyles.field}>
        <InputLabel id="demo-simple-select-label" sx={{ ...darkerGreyOnForms }}>
          Región
        </InputLabel>
        <Select
          labelId="demo-simple-select-label"
          label="Región"
          fullWidth
          id="region"
          name="region"
          onChange={onChangeRegion}
          value={controlRegion}
          error={!!errors.region[0]}>
          {regionsList.length ? (
            regionsList.map((item) => (
              <MenuItem
                key={item.externalId}
                value={item.externalId}
                sx={SchedulerStepsStyles.menuItem}>
                {item.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled sx={SchedulerStepsStyles.menuItem}>
              No hay datos disponibles.
            </MenuItem>
          )}
        </Select>
        <FormHelperText error={!!errors.region[0]}>{errors.region[0]}</FormHelperText>
      </FormControl>
      <FormControl fullWidth size="small" sx={SchedulerStepsStyles.field}>
        <InputLabel id="demo-simple-select-label" sx={{ ...darkerGreyOnForms }}>
          Seleccione taller
        </InputLabel>
        <Select
          labelId="demo-simple-select-label"
          label="Seleccione taller"
          fullWidth
          id="workshop"
          name="workshop"
          onChange={onChangeSelect}
          value={controlWorkshop}
          disabled={controlRegion === ''}
          error={!!errors.workshop[0]}>
          {filteredWorkshops.length > 0 ? (
            filteredWorkshops.map((item) => (
              <MenuItem key={item.id} value={item.id} sx={SchedulerStepsStyles.menuItem}>
                {item.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled sx={SchedulerStepsStyles.menuItem}>
              No hay talleres disponibles en esta región.
            </MenuItem>
          )}
        </Select>
        <FormHelperText error={!!errors.workshop[0]}>{errors.workshop[0]}</FormHelperText>
      </FormControl>
      <input type="submit" style={{ display: 'none' }} ref={submitButtonRef} />
    </>
  );
}

LocationForm.propTypes = {
  submitButtonRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

LocationForm.defaultProps = {
  submitButtonRef: { current: null },
};

export default LocationForm;
